<!--suppress JSNonASCIINames -->
<template>
  <div id="print-report">
    <!-- 검색 폼 -->
    <search-nav @handleSearch="onClickSearch">
      <slot name="search">
        <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
          <component
            v-for="(component, index) in stdSearchComponents"
            :key="`${index}_${component['value']}`"
            :is="component['component']"
            :label="component['label']"
            :query="component['query']"
            :options="component['options']"
            :onClickSearch="onClickSearch"
            v-model="search[component['value']]"
          />
        </div>
      </slot>
    </search-nav>

    <div class="flex w-full items-center mb-4">
      <vs-button class="ml-auto" type="border" color="secondary" @click="() => (showExcelDownload = true)">
        {{ $t("button.Excel 다운로드") }}
      </vs-button>
    </div>

    <table class="w-full">
      <thead>
        <tr>
          <th rowspan="2">부서명</th>
          <th rowspan="2">팀명</th>
          <th rowspan="2">사업장명</th>
          <th rowspan="2">성명</th>
          <th rowspan="2">최근 상담일</th>
          <th rowspan="2">혈압</th>
          <th rowspan="2">혈당</th>
          <th colspan="3">일반건강진단</th>
          <th colspan="8">배치전 건강진단</th>
          <th colspan="8">특수건강진단</th>
          <th rowspan="2">건강상담</th>
          <th v-if="!!month" rowspan="2">소계(월)</th>
          <th v-else rowspan="2">누계(년)</th>
        </tr>
        <tr>
          <th>A</th>
          <th>D2</th>
          <th>C2</th>

          <th>A</th>
          <th>C1</th>
          <th>C2</th>
          <th>CN</th>
          <th>D1</th>
          <th>D2</th>
          <th>DN</th>
          <th>R</th>

          <th>A</th>
          <th>C1</th>
          <th>C2</th>
          <th>CN</th>
          <th>D1</th>
          <th>D2</th>
          <th>DN</th>
          <th>R</th>
        </tr>
      </thead>

      <tbody>
        <!-- 로딩 상태 표시 -->
        <tr v-if="loading">
          <td colspan="25">
            <div class="w-full py-16 flex justify-center items-center bg-white shadow-sm rounded-md">
              <div class="text-center">
                <div class="spinner"></div>
                <p class="text-lg font-medium text-gray-700">데이터를 불러오는 중입니다...</p>
              </div>
            </div>
          </td>
        </tr>

        <!-- 데이터가 없는 경우 -->
        <tr v-else-if="!selectedForm || selectedForm.length === 0">
          <td colspan="25">
            <div class="w-full py-20 flex justify-center items-center bg-white shadow-sm rounded-md">
              <div class="text-center">
                <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 class="mt-4 text-lg font-medium text-gray-900">데이터가 없습니다</h3>
                <p class="mt-2 text-sm text-gray-500">
                  검색 조건에 맞는 데이터가 존재하지 않습니다.<br />
                  다른 검색 조건을 사용해보세요.
                </p>
              </div>
            </div>
          </td>
        </tr>

        <template v-else>
          <tr v-for="(row, rowIdx) of getFilteredList" :key="rowIdx">
            <td>{{ row.부서명 }}</td>
            <td>{{ row.팀명 }}</td>
            <td>{{ row.사업장명 }}</td>
            <td>{{ row.성명 }}</td>

            <td>{{ row.최근상담일 }}</td>
            <td>{{ row.혈압 }}</td>
            <td>{{ row.혈당 }}</td>

            <td>{{ row.일반_A }}</td>
            <td>{{ row.일반_D2 }}</td>
            <td>{{ row.일반_C2 }}</td>

            <td>{{ row.배치전_A }}</td>
            <td>{{ row.배치전_C1 }}</td>
            <td>{{ row.배치전_C2 }}</td>
            <td>{{ row.배치전_CN }}</td>
            <td>{{ row.배치전_D1 }}</td>
            <td>{{ row.배치전_D2 }}</td>
            <td>{{ row.배치전_DN }}</td>
            <td>{{ row.배치전_R }}</td>

            <td>{{ row.특수_A }}</td>
            <td>{{ row.특수_C1 }}</td>
            <td>{{ row.특수_C2 }}</td>
            <td>{{ row.특수_CN }}</td>
            <td>{{ row.특수_D1 }}</td>
            <td>{{ row.특수_D2 }}</td>
            <td>{{ row.특수_DN }}</td>
            <td>{{ row.특수_R }}</td>

            <td>{{ row.건강상담수 }}</td>
            <td>{{ row.소계 }}</td>
          </tr>

          <tr>
            <td colspan="7">합계</td>
            <td>{{ getSumRow.일반_A }}</td>
            <td>{{ getSumRow.일반_D2 }}</td>
            <td>{{ getSumRow.일반_C2 }}</td>

            <td>{{ getSumRow.배치전_A }}</td>
            <td>{{ getSumRow.배치전_C1 }}</td>
            <td>{{ getSumRow.배치전_C2 }}</td>
            <td>{{ getSumRow.배치전_CN }}</td>
            <td>{{ getSumRow.배치전_D1 }}</td>
            <td>{{ getSumRow.배치전_D2 }}</td>
            <td>{{ getSumRow.배치전_DN }}</td>
            <td>{{ getSumRow.배치전_R }}</td>

            <td>{{ getSumRow.특수_A }}</td>
            <td>{{ getSumRow.특수_C1 }}</td>
            <td>{{ getSumRow.특수_C2 }}</td>
            <td>{{ getSumRow.특수_CN }}</td>
            <td>{{ getSumRow.특수_D1 }}</td>
            <td>{{ getSumRow.특수_D2 }}</td>
            <td>{{ getSumRow.특수_DN }}</td>
            <td>{{ getSumRow.특수_R }}</td>

            <td>{{ getSumRow.건강상담수 }}</td>
            <td>{{ getSumRow.소계 }}</td>
          </tr>
        </template>
      </tbody>
    </table>

    <popup-excel-download v-model="showExcelDownload" :list="getExcelList" :title="getExcelFileName" :show-header="false" />
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchYear from "@/views/list/components/SearchYear.vue";
import SearchMonth from "@/views/list/components/SearchMonth.vue";
import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";
import SearchSelectCompany from "@/views/list/components/SearchSelectCompanyName.vue";

import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PopupPlanProject from "@/views/project/popup/PopupPlanProject";
import DateSelector from "@/components/selector/DateSelector";
import PopupProjectSetting from "@/views/project/PopupProjectSetting";
import MemberSelector from "@/components/selector/MemberSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import FileUploader from "@/components/FileUploader";
import FileUploadModule from "@/views/modules/FileUploadModule";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";

export default {
  name: "Viewer452",
  components: {
    SearchNav,
    SearchYear,
    SearchMonth,
    SearchColumnKeyword,
    SearchSelectCompany,

    PopupExcelDownload,
    FieldSelectModule,
    FileUploader,
    CompanySelector,
    MemberSelector,
    PopupProjectSetting,
    DateSelector,
    PopupPlanProject,
    FileUploadModule,
  },
  props: {
    tab: Object,
    board: Object,
    value: Object,
    header: Object,
  },
  data() {
    return {
      loading: false,

      // === Search ===
      search: {
        year: "",
        month: "",
        column: "",
        companyName: "",
      },

      showExcelDownload: false,
      isPrint: false,
      oldForm: {},
      year: new Date().getFullYear(),
      month: "",
      companyList: [],
      cachedData: undefined,
      selectedCompanyId: 0,
      fieldList: [], // 선택된 업체와 연관된 현장 ID
      selectedFieldId: 0, // 선택된 현장 ID
      selectedForm: [],
      fieldData: {},
      // API에서 불러온 통계 데이터
      classifications: ["공무원", "공무직", "임기제", "기간제", "기능직", "근로자", "공공근로", "노인일자리", "장애인일자리", "지역공동체", "합계"],
      // checkTypes: ['일반', '배치전', '배치후', '특수'],
      checkTypes: ["일반", "배치전", "특수"],
      definitions: {
        일반: ["", "C2", "D2"],
        배치전: ["C1", "C2", "CN", "D1", "D2", "DN", "R"],
        배치후: ["C1", "C2", "CN", "D1", "D2", "DN", "R"],
        특수: ["C1", "C2", "CN", "D1", "D2", "DN", "R", "소계", "누계"],
      },

      form: {
        total: {},
        stdInfo: {},
        fileList: [],
        totalB: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        inputB: [
          { label: "안전·보건관리자 임금 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "안전시설비 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "보호구 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "안전보건진단비 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "안전보건교육비 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "근로자 건강장해예방비 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "건설재해예방전문지도기관 기술지도비", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "본사 전담조직 근로자 임금 등", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "위험성평가 등에 따른 소요비용", value1: 0, value2: 0, value3: 0, value4: "0.00" },
        ],
        inputC: [
          { label: "안전관리계획서 작성비", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "현장안전점검비", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "공사장 주변 안전관리 사용비", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "통행안전 및 교통소통 대책비", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "공사 중 구조적 안전성 확보비용", value1: 0, value2: 0, value3: 0, value4: "0.00" },
          { label: "기타(안전관리비)", value1: 0, value2: 0, value3: 0, value4: "0.00" },
        ],
      },
    };
  },
  computed: {
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    projectId() {
      return parseInt(this.$route.params.proj_id) || 0;
    },
    postId() {
      return parseInt(this.$route.params.postId);
    },
    boardId() {
      return this.$route.params.boardId;
    },
    stdSearchComponents() {
      return [
        {
          component: "search-year",
          label: "연도",
          value: "year",
          query: { y: "year" },
          options: {
            default: new Date().getFullYear(),
          },
        },
        {
          component: "search-month",
          label: "월",
          value: "month",
          query: { y_m: "month" },
          options: {
            default: "",
            lastLabel: true,
            useAll: true,
          },
        },
        {
          component: "search-column-keyword",
          label: "검색조건",
          value: "column",
          query: { ck_t: "head", ck_k: "keyword" },
          options: {
            hiddenList: ["종사자 사업장명", "부서명", "대상년월", "작성일", "작성일(수정일)", "작성자", "구분", "직종", "종사자 부서", "ID(사번)"],
            additionalList: ["전체", "부서명", "팀명", "사업장명", "성명"],
          },
        },
        {
          component: "search-select-company",
          label: "부서명",
          value: "company",
          query: { c_id: "companyId", c_name: "companyName" },
          options: {
            visibleKeyValue: [{ fieldCompany: "부서별" }],
          },
        },
      ];
    },
    getFilteredList() {
      // Start with the full selectedForm
      let filteredList = this.selectedForm;

      // Check if search criteria exist
      if (this.search) {
        const { column, company } = this.search;

        // Filter by column.head and keyword
        if (column && column.head && column.keyword && column.keyword.trim() !== "") {
          const keyword = column.keyword.toLowerCase().trim();

          if (column.head === "전체") {
            // Filter items where keyword matches any field
            filteredList = filteredList.filter((item) => {
              return (
                (item.부서명 && item.부서명.toLowerCase().includes(keyword)) ||
                (item.팀명 && item.팀명.toLowerCase().includes(keyword)) ||
                (item.사업장명 && item.사업장명.toLowerCase().includes(keyword)) ||
                (item.성명 && item.성명.toLowerCase().includes(keyword))
              );
            });
          } else if (column.head === "부서명") {
            filteredList = filteredList.filter((item) => item.부서명 && item.부서명.toLowerCase().includes(keyword));
          } else if (column.head === "팀명") {
            filteredList = filteredList.filter((item) => item.팀명 && item.팀명.toLowerCase().includes(keyword));
          } else if (column.head === "사업장명") {
            filteredList = filteredList.filter((item) => item.사업장명 && item.사업장명.toLowerCase().includes(keyword));
          } else if (column.head === "성명") {
            filteredList = filteredList.filter((item) => item.성명 && item.성명.toLowerCase().includes(keyword));
          }
        }

        // Additional filter by company (equivalent to filtering by 부서명)
        if (company && company.trim && company.trim() !== "") {
          const companyKeyword = company.toLowerCase().trim();
          filteredList = filteredList.filter((item) => item.부서명 && item.부서명.toLowerCase().includes(companyKeyword));
        }
      }
      console.log("filteredList", filteredList);
      return filteredList;
    },
    /**
     * selectedForm 데이터의 일반_A부터 특수_R까지의 값을 각각 합산하여 반환하는 메서드
     */
    getSumRow() {
      return {
        일반_A: this.getFilteredList.reduce((acc, row) => acc + (row.일반_A || 0), 0),
        일반_C2: this.getFilteredList.reduce((acc, row) => acc + (row.일반_C2 || 0), 0),
        일반_D2: this.getFilteredList.reduce((acc, row) => acc + (row.일반_D2 || 0), 0),
        배치전_A: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_A || 0), 0),
        배치전_C1: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_C1 || 0), 0),
        배치전_C2: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_C2 || 0), 0),
        배치전_CN: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_CN || 0), 0),
        배치전_D1: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_D1 || 0), 0),
        배치전_D2: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_D2 || 0), 0),
        배치전_DN: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_DN || 0), 0),
        배치전_R: this.getFilteredList.reduce((acc, row) => acc + (row.배치전_R || 0), 0),
        특수_A: this.getFilteredList.reduce((acc, row) => acc + (row.특수_A || 0), 0),
        특수_C1: this.getFilteredList.reduce((acc, row) => acc + (row.특수_C1 || 0), 0),
        특수_C2: this.getFilteredList.reduce((acc, row) => acc + (row.특수_C2 || 0), 0),
        특수_CN: this.getFilteredList.reduce((acc, row) => acc + (row.특수_CN || 0), 0),
        특수_D1: this.getFilteredList.reduce((acc, row) => acc + (row.특수_D1 || 0), 0),
        특수_D2: this.getFilteredList.reduce((acc, row) => acc + (row.특수_D2 || 0), 0),
        특수_DN: this.getFilteredList.reduce((acc, row) => acc + (row.특수_DN || 0), 0),
        특수_R: this.getFilteredList.reduce((acc, row) => acc + (row.특수_R || 0), 0),
        건강상담수: this.getFilteredList.reduce((acc, row) => acc + (row.건강상담수 || 0), 0),
        소계: this.getFilteredList.reduce((acc, row) => acc + (row.소계 || 0), 0),
      };
    },
    getExcelList() {
      const result = [
        {
          부서명: "",
          팀명: "",
          사업장명: "",
          성명: "",
          최근상담일: "",
          혈압: "",
          혈당: "",
          "일반.A": "일반건강진단",
          "일반.C2": "",
          "일반.D2": "",
          "배치전.A": "배치전 건강진단",
          "배치전.C1": "",
          "배치전.C2": "",
          "배치전.CN": "",
          "배치전.D1": "",
          "배치전.D2": "",
          "배치전.DN": "",
          "배치전.R": "",
          "특수.A": "특수건강진단",
          "특수.C1": "",
          "특수.C2": "",
          "특수.CN": "",
          "특수.D1": "",
          "특수.D2": "",
          "특수.DN": "",
          "특수.R": "",
          건강상담수: "",
          소계: "",
        },
        {
          부서명: "부서명",
          팀명: "팀명",
          사업장명: "사업장명",
          성명: "성명",
          최근상담일: "최근상담일",
          혈압: "혈압",
          혈당: "혈당",
          "일반.A": "A",
          "일반.C2": "C2",
          "일반.D2": "D2",
          "배치전.A": "A",
          "배치전.C1": "C1",
          "배치전.C2": "C2",
          "배치전.CN": "CN",
          "배치전.D1": "D1",
          "배치전.D2": "D2",
          "배치전.DN": "DN",
          "배치전.R": "R",
          "특수.A": "A",
          "특수.C1": "C1",
          "특수.C2": "C2",
          "특수.CN": "CN",
          "특수.D1": "D1",
          "특수.D2": "D2",
          "특수.DN": "DN",
          "특수.R": "R",
          건강상담수: "건강상담",
          소계: this.month ? "소계(월)" : "누계(년)",
        },
      ];

      if (this.getFilteredList && this.getFilteredList.length > 0) {
        for (const row of this.getFilteredList) {
          result.push({
            부서명: row.부서명,
            팀명: row.팀명,
            사업장명: row.사업장명,
            성명: row.성명,
            최근상담일: row.최근상담일,
            혈압: row.혈압,
            혈당: row.혈당,
            "일반.A": row.일반_A,
            "일반.C2": row.일반_C2,
            "일반.D2": row.일반_D2,
            "배치전.A": row.배치전_A,
            "배치전.C1": row.배치전_C1,
            "배치전.C2": row.배치전_C2,
            "배치전.CN": row.배치전_CN,
            "배치전.D1": row.배치전_D1,
            "배치전.D2": row.배치전_D2,
            "배치전.DN": row.배치전_DN,
            "배치전.R": row.배치전_R,
            "특수.A": row.특수_A,
            "특수.C1": row.특수_C1,
            "특수.C2": row.특수_C2,
            "특수.CN": row.특수_CN,
            "특수.D1": row.특수_D1,
            "특수.D2": row.특수_D2,
            "특수.DN": row.특수_DN,
            "특수.R": row.특수_R,
            건강상담수: row.건강상담수,
            소계: row.소계,
          });
        }
      }

      return result;
    },
    getExcelFileName() {
      let title = "유소견자 건강 상담 실적 통계";
      title += `_${this.year}년`;
      if (this.month) {
        title += `_${this.month}월`;
      }
      return title;
    },
  },
  watch: {
    year: {
      handler() {
        this.loadListBoard();
      },
    },
    month: {
      handler() {
        this.loadListBoard();
      },
    },
    selectedCompanyId: {
      handler() {
        this.loadListBoard();
      },
    },
    selectedFieldId: {
      handler() {
        console.log("selectedFieldId", this.selectedFieldId);
      },
    },
  },
  created() {
    this.$store.dispatch("company/LOAD_COMPANY_TREE", { projectId: this.projectId });
    this.initBreadcrumb();
  },
  mounted() {
    this.loadListBoard();
  },
  methods: {
    initBreadcrumb() {
      if (this.boardId === "_361") {
        this.$store.commit("menu/UPDATE_PAGE_LIST", [
          { title: "Home" },
          { title: "건강진단 현황" },
          {
            title: "유소견자 건강 상담 실적 통계",
            note: "「산업안전보건법」제39조제1항(보건조치), 「산업안전보건기준에 관한 규칙」제657조(유해요인 조사)",
            active: true,
          },
        ]);
      } else {
        this.$store.commit("menu/UPDATE_PAGE_LIST", [
          { title: "Home" },
          { title: "건강진단 현황" },
          {
            title: "유소견자 건강 상담 실적 통계",
            active: true,
          },
        ]);
      }
    },
    onClickSearch() {
      this.year = this.search.year;
      this.month = this.search.month;

      this.loadListBoard();
    },
    async loadListBoard() {
      try {
        this.loading = true; // 로딩 시작

        const projectId = this.projectId;
        const boardId = 452;
        let page = 1;
        const perPage = 10000;
        const conditions = [];

        // 게시글 불러온다
        const targetYear = this.year;

        let monthFrom = `${this.month}`.padStart(2, "0");
        let monthTo = `${this.month}`.padStart(2, "0");
        let endDay = "31";
        if (!this.month) {
          monthFrom = "01";
          monthTo = "12";
        } else {
          endDay = new Date(targetYear, this.month, 0).getDate();
        }

        conditions.push({ key: "body.건강상담[%].input_7", operator: ">=", value: `${targetYear}-${monthFrom}-01` });
        conditions.push({ key: "body.건강상담[%].input_7", operator: "<=", value: `${targetYear}-${monthTo}-${endDay}` });

        const allData = [];

        while (page <= 100) {
          const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId,
            boardId,
            page,
            perPage,
            conditions,
            withMeta: true,
            metaKeys: [
              "body.enc.inputA_7", // 성명
              // 'body.건강상담[%].input_21.%', // 검진 항목
              "body.employee_company.com_name", // 팀명
              "body.employee_fields[%].%", // 사업장, 부서명
              "body.건강상담[%].%", // 건강상담 전체
            ],
            onlyMeta: true,
            withoutRelation: true,
          });

          const pageData = response.data || [];
          allData.push(...pageData);

          // 다음 페이지가 없으면 종료
          if (response.current_page === response.last_page) {
            break;
          }
          page += 1;
        }

        this.parseData(allData);
      } catch (error) {
        console.error("데이터 로드 중 오류 발생:", error);
      } finally {
        this.loading = false; // 로딩 종료
      }
    },
    // 통계 데이터를 테이블 형식에 맞게 변환한다
    parseData(allData) {
      this.selectedForm = [];

      for (const row of allData) {
        // 예시 :
        // {"body":{"employee_company":{"com_name":"산림조성팀"},"employee_fields":[{"field_name":"산림병해충예찰방제단","id":"140","부서명":"녹지공원과"}],"enc":{"inputA_2":"만 71세"},"건강상담":[{"input_21":{"배치전 검진":["C1","D2"],"일반검진":["D2","C2"],"특수검진":["CN","D1"]}}]}}
        const content = row.postContents;

        // employeeId 안전하게 가져오기
        let employeeId = "";
        if (content && content.body && content.body.inputA_1) {
          employeeId = content.body.inputA_1;
        } else if (content && content.body && content.body.employee && content.body.employee.employee_id) {
          employeeId = content.body.employee.employee_id;
        } else {
          employeeId = Math.random().toString(36).substring(2, 15);
        }

        // 기존 아이템이 있는지 확인
        let item = this.selectedForm.find((item) => item.employeeId === employeeId);

        if (!item) {
          // 새 아이템 생성
          item = {
            employeeId,
            부서명: "",
            팀명: "",
            사업장명: "",
            성명: "",
            최근상담일: "",
            혈압: "",
            혈당: "",
            일반_A: 0,
            일반_D2: 0,
            일반_C2: 0,
            배치전_A: 0,
            배치전_C1: 0,
            배치전_C2: 0,
            배치전_CN: 0,
            배치전_D1: 0,
            배치전_D2: 0,
            배치전_DN: 0,
            배치전_R: 0,
            특수_A: 0,
            특수_C1: 0,
            특수_C2: 0,
            특수_CN: 0,
            특수_D1: 0,
            특수_D2: 0,
            특수_DN: 0,
            특수_R: 0,
            소계: 0,

            건강상담수: 0, // 건강상담 수
          };

          // 부서명 안전하게 가져오기
          if (content && content.body && content.body.employee_fields && content.body.employee_fields.length > 0 && content.body.employee_fields[0].부서명) {
            item.부서명 = content.body.employee_fields[0].부서명;
          }

          // 팀명 안전하게 가져오기
          if (content && content.body && content.body.employee_company && content.body.employee_company.com_name) {
            item.팀명 = content.body.employee_company.com_name;
          }

          // 사업장명 안전하게 가져오기
          if (content && content.body && content.body.employee_fields && content.body.employee_fields.length > 0 && content.body.employee_fields[0].field_name) {
            item.사업장명 = content.body.employee_fields[0].field_name;
          }

          // 성명 안전하게 가져오기
          if (content && content.body && content.body.enc && content.body.enc.inputA_7) {
            item.성명 = content.body.enc.inputA_7;
          }

          if (content && content.body && content.body.건강상담) {
            // 가장 최근의 건강상담을 가져옴
            const 최근데이터 = content.body.건강상담.reduce(function (latest, current) {
              if (!latest) {
                return current;
              }

              const latestDate = new Date(latest.input_7);
              const currentDate = new Date(current.input_7);

              return currentDate > latestDate ? current : latest;
            }, null);
            if (최근데이터) {
              // 최근상담일
              item.최근상담일 = 최근데이터.input_7;
              // 혈압
              item.혈압 = 최근데이터.input_15;
              // 혈당
              item.혈당 = 최근데이터.input_16;
            }
          }

          // 새 아이템 배열에 추가
          this.selectedForm.push(item);
        }

        // 건강상담 데이터 처리
        if (content && content.body && content.body.건강상담) {
          const 건강상담 = content.body.건강상담;

          // 각 건강상담 항목 처리
          for (let i = 0; i < 건강상담.length; i++) {
            item.건강상담수 += 1; // 건강상담 수 증가
            const 상담항목 = 건강상담[i];

            // input_21 확인
            if (상담항목 && 상담항목.input_21) {
              const 검진결과 = 상담항목.input_21;

              // 일반검진 처리
              if (검진결과 && 검진결과["일반검진"]) {
                const 일반검진결과 = 검진결과["일반검진"];

                for (let j = 0; j < 일반검진결과.length; j++) {
                  const 결과 = 일반검진결과[j];

                  // 각 카테고리별 카운트 증가
                  if (결과 === "A") item.일반_A += 1;
                  if (결과 === "D2") item.일반_D2 += 1;
                  if (결과 === "C2") item.일반_C2 += 1;
                }
              }

              // 배치전 검진 처리
              if (검진결과 && 검진결과["배치전 검진"]) {
                const 배치전검진결과 = 검진결과["배치전 검진"];

                for (let j = 0; j < 배치전검진결과.length; j++) {
                  const 결과 = 배치전검진결과[j];

                  // 각 카테고리별 카운트 증가
                  if (결과 === "A") item.배치전_A += 1;
                  if (결과 === "C1") item.배치전_C1 += 1;
                  if (결과 === "C2") item.배치전_C2 += 1;
                  if (결과 === "CN") item.배치전_CN += 1;
                  if (결과 === "D1") item.배치전_D1 += 1;
                  if (결과 === "D2") item.배치전_D2 += 1;
                  if (결과 === "DN") item.배치전_DN += 1;
                  if (결과 === "R") item.배치전_R += 1;
                }
              }

              // 특수검진 처리
              if (검진결과 && 검진결과["특수검진"]) {
                const 특수검진결과 = 검진결과["특수검진"];

                for (let j = 0; j < 특수검진결과.length; j++) {
                  const 결과 = 특수검진결과[j];

                  // 각 카테고리별 카운트 증가
                  if (결과 === "A") item.특수_A += 1;
                  if (결과 === "C1") item.특수_C1 += 1;
                  if (결과 === "C2") item.특수_C2 += 1;
                  if (결과 === "CN") item.특수_CN += 1;
                  if (결과 === "D1") item.특수_D1 += 1;
                  if (결과 === "D2") item.특수_D2 += 1;
                  if (결과 === "DN") item.특수_DN += 1;
                  if (결과 === "R") item.특수_R += 1;
                }
              }
            }
          }
        }

        // 소계 계산
        item.소계 =
          item.일반_A +
          item.일반_D2 +
          item.일반_C2 +
          item.배치전_A +
          item.배치전_C1 +
          item.배치전_C2 +
          item.배치전_CN +
          item.배치전_D1 +
          item.배치전_D2 +
          item.배치전_DN +
          item.배치전_R +
          item.특수_A +
          item.특수_C1 +
          item.특수_C2 +
          item.특수_CN +
          item.특수_D1 +
          item.특수_D2 +
          item.특수_DN +
          item.특수_R;
      }
    },

    printLandscape() {
      this.print("@media print {@page { size: a3 landscape; }}");
    },
    printPortrait() {
      this.print("@media print {@page { size: a3 portrait; }}");
    },
    print(css) {
      // 프린트
      this.isPrint = true;

      setTimeout(() => {
        const printTextarea = document.getElementsByClassName("print-textarea");
        for (let i = 0; i < printTextarea.length; i++) {
          document.getElementsByClassName("print-textarea")[i].innerHTML = printTextarea[i].innerHTML.trimStart().trimEnd().replace(/\r\n/gi, "<br>").replace(/\\n/gi, "<br>").replace(/\n/gi, "<br>");
        }

        const hidePrintTitleEl = document.getElementById("hidePrintTitle");
        const hidePrintTitle = hidePrintTitleEl && hidePrintTitleEl.value === "true";

        const g_label = document.getElementById("print-label") ? document.getElementById("print-label").innerHTML : "";
        const g_oBeforeBody = document.getElementById("print-report").innerHTML;

        const beforeApprovalEl = document.getElementById("print-approval");
        const g_oBeforeApproval = beforeApprovalEl ? beforeApprovalEl.innerHTML : "";

        // 프린트를 보이는 그대로 나오기위한 셋팅
        window.onbeforeprint = function () {
          document.body.style.backgroundColor = "#fff";
          document.body.innerHTML = "";
          if (g_label && this.isPrint && !hidePrintTitle) {
            document.body.innerHTML += `${g_label}<br>`;
          }
          document.body.innerHTML += `${g_oBeforeBody}<br>${g_oBeforeApproval}`;
        };

        const head = document.head || document.getElementsByTagName("head")[0],
          style = document.createElement("style");

        style.type = "text/css";
        style.media = "print";

        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(document.createTextNode(css));
        }

        head.appendChild(style);

        window.print();
        location.reload();
      }, 300);
    },
    onClickPrint() {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: "레이아웃 선택",
          messageIcon: "AlertCircleIcon",
          message: "인쇄 방향을 선택해 주세요.",
          useConfirm: true,
          confirmBtn: "세로 방향",
          cancelBtn: "가로 방향",
          confirmFunction: () => this.printPortrait(),
          cancelFunction: () => this.printLandscape(),
          size: "small",
          btnPosition: "center",
          confirmBtnStyle: {
            type: "border",
            icon: { type: "image", name: "icon-print-layout-vertical.svg" },
            style: "",
          },
          cancelBtnStyle: {
            type: "border",
            icon: { type: "image", name: "icon-print-layout-horizontal.svg" },
            style: "",
          },
        },
      });
      window.dispatchEvent(customAlert);
    },
    getContent(data) {
      if (data.post_content) {
        const content = JSON.parse(data.post_content);
        return content.body;
      }
    },
    getHeader(data) {
      if (data.post_content) {
        const content = JSON.parse(data.post_content);
        return content.header;
      }
    },
    comma(str) {
      return `${str}`.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    removeComma(str) {
      if (str) {
        const noneComma = `${str}`.replaceAll(",", "");
        return `${Number(noneComma)}`;
      }
      return "0";
    },
    percentage(a, b) {
      // A를 B로 나눈 결과를 구합니다.
      const result = b !== 0 ? (a / b) * 100 : 0;
      // 소수점 2자리까지 반올림합니다.
      const roundedResult = Math.round(result * 100) / 100;
      // 문자열로 변환합니다.
      const resultString = roundedResult.toString();
      // 소수점이 없는 경우 '.00'을 덧붙입니다.
      if (!resultString.includes(".")) {
        return `${resultString}.00`;
      }
      // 소수점이 하나만 있는 경우 '0'을 덧붙입니다.
      if (resultString.split(".")[1].length === 1) {
        return `${resultString}0`;
      }
      // 소수점이 둘 이상인 경우 그대로 반환합니다.
      return resultString;
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  th,
  td {
    min-height: 54px;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: center;
    vertical-align: middle;
    border: 1px solid #e5e7eb;
    padding: 12px 4px;
    transition: background-color 0.2s;
  }

  th {
    font-weight: 500;
    background-color: #f3f3f3;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  td {
    font-weight: 400;
    background-color: white;
  }

  tbody tr:hover td {
    background-color: #efefef;
  }

  tbody tr:last-child td {
    background-color: #f9f9f9;
    font-weight: 500;
  }
}

.spinner {
  display: inline-block;
  width: 48px;
  height: 48px;
  border: 4px solid #cdcdcd;
  border-top-color: #4776e6;
  border-radius: 50%;
  margin-bottom: 16px;
  animation: spinner 1s linear infinite;
}

// 로딩 애니메이션
@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
